/**
 * The lazy block surfaces a chat message can render: a `chart` fence, a
 * `mermaid` fence, and a figure image.
 *
 * The sibling of chatCode.tsx, and split out of BirdMessage for the same
 * reason it was: each of these is a lazy chunk plus the identical
 * ErrorBoundary + Suspense boundary, and repeating that plumbing inline in
 * the markdown component map buried the routing decision underneath it.
 * BirdMessage still owns WHICH of these a fence resolves to (including the
 * `isStreaming` gate on mermaid, whose reasoning belongs with the router);
 * this module owns the loading and failure treatment for each one.
 *
 * Every block is lazy because none of them is on the common path: most turns
 * carry no chart, no diagram and no figure, and the three dependencies behind
 * them (Recharts, mermaid, the attachment fetch) are the heavy part.
 */

import { Box, Skeleton } from '@chakra-ui/react';
import { Suspense } from 'react';

import { lazyWithRetry } from '../../../utils/lazyWithRetry';
import { ErrorBoundary, ComponentErrorFallback } from '../../ErrorBoundary';
import type { ChartBlock as ChartBlockType } from './ChartBlock';
import type { FigureBlock as FigureBlockType } from './FigureBlock';
import type { MermaidBlock as MermaidBlockType } from './MermaidBlock';
import { BowerSpinner } from '../../ui/BowerSpinner';

// Each factory is exported for the same reason as `loadChatCodeBlock` in
// chatCode.tsx: a test warms the chunk through this factory rather than
// re-declaring the specifier, so a repoint here can't silently desync it.
export const loadChartBlock = () =>
  import('./ChartBlock').then((m) => ({ default: m.ChartBlock as typeof ChartBlockType }));

export const loadFigureBlock = () =>
  import('./FigureBlock').then((m) => ({ default: m.FigureBlock as typeof FigureBlockType }));

// MermaidBlock pulls in chartSpec.ts (which imports zod) and mermaid itself,
// both sizeable, so this stays lazy rather than landing in the chat entry
// bundle.
export const loadMermaidBlock = () =>
  import('./MermaidBlock').then((m) => ({ default: m.MermaidBlock as typeof MermaidBlockType }));

const ChartBlock = lazyWithRetry(loadChartBlock);
const FigureBlock = lazyWithRetry(loadFigureBlock);
const MermaidBlock = lazyWithRetry(loadMermaidBlock);

export function ChatChartBlock({ spec }: { readonly spec: string }) {
  return (
    <ErrorBoundary level="component" fallback={<ComponentErrorFallback />}>
      <Suspense
        fallback={
          <Box h="220px" display="flex" alignItems="center" justifyContent="center">
            <BowerSpinner size="sm" />
          </Box>
        }
      >
        <ChartBlock spec={spec} />
      </Suspense>
    </ErrorBoundary>
  );
}

export function ChatMermaidBlock({ code }: { readonly code: string }) {
  return (
    <ErrorBoundary level="component" fallback={<ComponentErrorFallback />}>
      <Suspense fallback={<Skeleton h="120px" borderRadius="10px" my={3} />}>
        <MermaidBlock code={code} />
      </Suspense>
    </ErrorBoundary>
  );
}

export function ChatFigureBlock({
  attachmentId,
  caption,
}: {
  readonly attachmentId: string;
  readonly caption: string;
}) {
  return (
    <ErrorBoundary level="component" fallback={<ComponentErrorFallback />}>
      <Suspense fallback={<Skeleton h="200px" borderRadius="10px" my={3} />}>
        <FigureBlock attachmentId={attachmentId} caption={caption} />
      </Suspense>
    </ErrorBoundary>
  );
}
